import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ISuperTableCalculation, ISuperTableCalculationFunction } from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IXAxisConfigColumn, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { SuperTableColumnBelongingYear } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/super_table_column_belonging_year.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash-es';
import { FIRST_ELEMENT_INDEX, NumberUtils } from '../../../../../../../aumico-lib-core-fe/src/lib';
import { ISuperTableAddCalculationResult } from '../../../../interfaces/super-table-add-calculation-result.interface';
import { ISuperTableItemSelection } from '../../../../interfaces/super-table-item-selection.interface';
import { SuperTableColumnWidth } from '@modeso/aumico-lib-notes-fe/enums/SuperTableColumnWidth.ebum';

@UntilDestroy()
@Component({
    selector: 'super-table-ratio-column',
    templateUrl: './super-table-ratio-column.component.html',
    styleUrls: ['./super-table-ratio-column.component.scss'],
})
export class SuperTableRatioColumnComponent implements OnInit, OnChanges {
    @Input() columnSelectionOptions: IXAxisConfigColumn[] = [];
    @Input() rowSelectionOptions: ISuperTableItemSelection[] = [];
    @Input() existingCalculation: ISuperTableCalculation | undefined;
    @Input() language: string | undefined;

    existingSelectedColumn: INameId | undefined;
    existingSelectedRow: ISuperTableItemSelection | undefined;

    selectedRowItem: ISuperTableItemSelection | undefined;
    selectedColumnItem: INameId | undefined;

    titleControl = new FormControl('');
    columnWidthControl = new FormControl(SuperTableColumnWidth.SMALL);

    belongingYearControl = new FormControl(SuperTableColumnBelongingYear.NO_YEAR);
    belongingYearSelections: INameId[] = [];
    nameIdColSelections: INameId[] = [];
    columnWidthSelections: INameId[] = [];

    @Output() calculationOutput: EventEmitter<ISuperTableAddCalculationResult> =
        new EventEmitter<ISuperTableAddCalculationResult>();

    constructor(private localizedTexts: I18NextService) {
        this.prepareBelongingYearSelections();
        this.prepareColumnWidthSelections();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['columnSelectionOptions']?.currentValue) {
            this.prepareNameIdColSelections();
        }

        if (changes['existingCalculation']?.currentValue) {
            this.titleControl.setValue(this.existingCalculation.title);

            const selectedColumn = this.existingCalculation.columnsIds[FIRST_ELEMENT_INDEX];

            this.selectedColumnItem = this.nameIdColSelections.find((option) => selectedColumn === option.id);
            this.existingSelectedColumn = cloneDeep<INameId[]>(this.selectedColumnItem);

            const selectedRow = this.existingCalculation.relationItemId;

            this.selectedRowItem = this.rowSelectionOptions.find((option) => selectedRow === option.name);
            this.existingSelectedRow = cloneDeep<INameId[]>(this.selectedRowItem);

            const existingCalculationColumn = this.columnSelectionOptions.find(
                (option) => option.columnId === this.existingCalculation.calcId,
            );

            this.belongingYearControl.setValue(existingCalculationColumn.belongingYear);

            this.columnWidthControl.setValue(existingCalculationColumn.columnWidth);

            this.calculationOutput.emit({
                title: this.titleControl.value,
                function: ISuperTableCalculationFunction.RATIO,
                relationItem: this.selectedRowItem,
                columnSelections: [this.selectedColumnItem],
                rowSelections: this.rowSelectionOptions, // apply for all rows
                belongingYear: this.belongingYearControl.value,
                columnWidth: this.columnWidthControl.value,
            });
        }
    }

    ngOnInit(): void {
        this.titleControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            this.calculationOutput.emit({
                title: value,
                function: ISuperTableCalculationFunction.RATIO,
                relationItem: this.selectedRowItem,
                columnSelections: [this.selectedColumnItem],
                rowSelections: this.rowSelectionOptions, // apply for all rows
                belongingYear: this.belongingYearControl.value,
                columnWidth: this.columnWidthControl.value,
            });
        });

        this.belongingYearControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            this.calculationOutput.emit({
                title: this.titleControl.value,
                function: ISuperTableCalculationFunction.RATIO,
                relationItem: this.selectedRowItem,
                columnSelections: [this.selectedColumnItem],
                rowSelections: this.rowSelectionOptions, // apply for all rows
                belongingYear: value,
                columnWidth: this.columnWidthControl.value,
            });
        });
        this.columnWidthControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            this.calculationOutput.emit({
                title: this.titleControl.value,
                function: ISuperTableCalculationFunction.RATIO,
                relationItem: this.selectedRowItem,
                columnSelections: [this.selectedColumnItem],
                rowSelections: this.rowSelectionOptions, // apply for all rows
                belongingYear: this.belongingYearControl.value,
                columnWidth: value,
            });
        });
    }

    onRelationCellClick(selectedRow: ISuperTableItemSelection, selectedColumn: IXAxisConfigColumn): void {
        if (
            selectedRow.name === this.selectedRowItem?.name &&
            selectedColumn.columnId === this.selectedColumnItem?.id
        ) {
            this.selectedColumnItem = null;
            this.selectedRowItem = null;
        } else {
            const nameIdColumn = this.nameIdColSelections.find((col) => col.id === selectedColumn.columnId);
            this.selectedColumnItem = nameIdColumn;
            this.selectedRowItem = selectedRow;
        }

        this.calculationOutput.emit({
            title: this.titleControl.value,
            function: ISuperTableCalculationFunction.RATIO,
            relationItem: this.selectedRowItem,
            columnSelections: [this.selectedColumnItem],
            rowSelections: this.rowSelectionOptions, // apply for all rows
            belongingYear: this.belongingYearControl.value,
            columnWidth: this.columnWidthControl.value,
        });
    }

    private prepareNameIdColSelections(): void {
        for (const col of this.columnSelectionOptions) {
            if (col.type === SuperTableColumnType.TEXT) {
                continue;
            }
            if (col.type === SuperTableColumnType.CURRENT_YEAR) {
                this.nameIdColSelections.push({
                    id: col.columnId,
                    name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_current_year') as string,
                });
                continue;
            }
            if (col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                this.nameIdColSelections.push({
                    id: col.columnId,
                    name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_previous_year') as string,
                });
                continue;
            }

            this.nameIdColSelections.push({
                id: col.columnId,
                name: col.title[this.language],
            });
        }
    }

    private prepareBelongingYearSelections(): void {
        this.belongingYearSelections.push(
            {
                id: SuperTableColumnBelongingYear.CURRENT_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_current') as string,
            },
            {
                id: SuperTableColumnBelongingYear.PREVIOUS_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_previous') as string,
            },
            {
                id: SuperTableColumnBelongingYear.NO_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_none') as string,
            },
        );
    }

    getCalculatedPercentage(row: ISuperTableItemSelection, column: IXAxisConfigColumn): number {
        const referenceValue =
            this.selectedColumnItem.id === 'CY'
                ? this.selectedRowItem.currentValue
                : this.selectedColumnItem.id === 'PV'
                ? this.selectedRowItem.previousValue
                : (this.selectedRowItem[this.selectedColumnItem.id] as number);

        const value: number =
            column.columnId === 'CY'
                ? row.currentValue
                : column.columnId === 'PV'
                ? row.previousValue
                : (row[this.selectedColumnItem.id] as number);

        if (
            NumberUtils.isZero(value) ||
            !NumberUtils.isFinite(value) ||
            NumberUtils.isZero(referenceValue) ||
            !NumberUtils.isFinite(referenceValue)
        ) {
            return undefined;
        }

        return NumberUtils.calculatePercentage(value, referenceValue);
    }

    private prepareColumnWidthSelections(): void {
        this.columnWidthSelections.push(
            {
                id: SuperTableColumnWidth.DYNAMIC,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_dynamic') as string,
            },
            {
                id: SuperTableColumnWidth.SMALL,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_small') as string,
            },
            {
                id: SuperTableColumnWidth.STANDARD,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_standard') as string,
            },
            {
                id: SuperTableColumnWidth.LARGE,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_large') as string,
            },
        );
    }
}
